import React from 'react';
import {
  AI_RESEARCH_LABS,
  SKILL_CATEGORIES,
  getMissingSkillsForLabs
} from '../data/aiResearchSkills';

const AIResearchGapAnalysis = ({
  userSkills = [],
  targetLabs = ['openai', 'anthropic', 'deepmind', 'metaFair'],
  maxSkills = 12,
  onSkillClick
}) => {
  const gaps = getMissingSkillsForLabs(userSkills, targetLabs);
  const displayGaps = gaps.slice(0, maxSkills);

  // Calculate total potential impact
  const totalPotential = displayGaps.reduce((sum, g) => sum + g.impact, 0);

  // Get lab names for display
  const labNames = targetLabs
    .map(key => AI_RESEARCH_LABS[key]?.shortName)
    .filter(Boolean)
    .join(', ');

  return (
    <div className="card" style={{ marginBottom: '20px', overflow: 'hidden' }}>
      {/* Header */}
      <div style={{
        padding: '16px',
        borderBottom: '1px solid #e5e5e5',
        background: 'linear-gradient(135deg, #dc2626 0%, #991b1b 100%)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '600', color: '#fff' }}>
              Your AI Research Skill Gaps
            </h3>
            <p style={{ margin: '4px 0 0', fontSize: '11px', color: 'rgba(255,255,255,0.8)' }}>
              Based on target labs: {labNames}
            </p>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ color: '#fca5a5', fontWeight: '600', fontSize: '14px' }}>
              {gaps.length} gaps
            </div>
            <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.7)' }}>
              +${(totalPotential / 1000).toFixed(0)}K potential
            </div>
          </div>
        </div>
      </div>

      {/* Gap List */}
      <div style={{ padding: '16px' }}>
        {displayGaps.map((gap, i) => {
          const category = SKILL_CATEGORIES[gap.category];
          const isPriority = i < 3;

          return (
            <div
              key={gap.skill}
              onClick={() => onSkillClick?.(gap.skill)}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '12px',
                marginBottom: '8px',
                background: isPriority ? 'rgba(220, 38, 38, 0.06)' : '#f9fafb',
                border: isPriority ? '1px solid rgba(220, 38, 38, 0.2)' : '1px solid #e5e5e5',
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'all 0.15s ease'
              }}
            >
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {isPriority && (
                    <span style={{
                      fontSize: '9px',
                      background: '#dc2626',
                      color: '#fff',
                      padding: '2px 6px',
                      borderRadius: '4px',
                      fontWeight: '600'
                    }}>
                      PRIORITY
                    </span>
                  )}
                  <span style={{
                    fontWeight: '500',
                    fontSize: '13px',
                    color: '#374151'
                  }}>
                    {gap.skill}
                  </span>
                </div>
                <div style={{
                  fontSize: '11px',
                  color: category?.color || '#6b7280',
                  marginTop: '4px'
                }}>
                  {category?.label || gap.category}
                  {gap.labRelevance > 0 && (
                    <span style={{ marginLeft: '8px', color: '#6b7280' }}>
                      • Lab match: {gap.labRelevance}%
                    </span>
                  )}
                </div>
              </div>

              <div style={{ textAlign: 'right' }}>
                <div style={{
                  color: '#059669',
                  fontWeight: '600',
                  fontSize: '14px'
                }}>
                  +${(gap.impact / 1000).toFixed(0)}K
                </div>
                <div style={{
                  fontSize: '10px',
                  color: '#9ca3af'
                }}>
                  Demand: {gap.demandScore}%
                </div>
              </div>
            </div>
          );
        })}

        {gaps.length > maxSkills && (
          <div style={{
            textAlign: 'center',
            padding: '8px',
            fontSize: '12px',
            color: '#6b7280'
          }}>
            +{gaps.length - maxSkills} more skills to develop
          </div>
        )}
      </div>

      {/* Skills You Have */}
      {userSkills.length > 0 && (
        <div style={{
          padding: '12px 16px',
          borderTop: '1px solid #e5e5e5',
          background: 'rgba(16, 185, 129, 0.05)'
        }}>
          <div style={{
            fontSize: '11px',
            color: '#059669',
            fontWeight: '600',
            marginBottom: '8px'
          }}>
            ✓ SKILLS YOU HAVE
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {userSkills.slice(0, 8).map(skill => (
              <span
                key={skill}
                style={{
                  fontSize: '11px',
                  padding: '4px 8px',
                  background: '#d1fae5',
                  color: '#059669',
                  borderRadius: '4px'
                }}
              >
                {skill}
              </span>
            ))}
            {userSkills.length > 8 && (
              <span style={{
                fontSize: '11px',
                padding: '4px 8px',
                color: '#6b7280'
              }}>
                +{userSkills.length - 8} more
              </span>
            )}
          </div>
        </div>
      )}

      {/* Action Footer */}
      <div style={{
        padding: '12px 16px',
        borderTop: '1px solid #e5e5e5',
        background: '#f9fafb',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div style={{ fontSize: '11px', color: '#6b7280' }}>
          Focus on <strong style={{ color: '#dc2626' }}>PRIORITY</strong> skills first for maximum impact
        </div>
        <div style={{
          fontSize: '12px',
          color: '#7c3aed',
          fontWeight: '500'
        }}>
          See Learning Paths →
        </div>
      </div>
    </div>
  );
};

export default AIResearchGapAnalysis;
